import { autoUpdate, computePosition, flip, offset } from "@floating-ui/dom";
import { Dom } from "./dom";

interface TooltipEntry
{
    title: string | (() => string);
    html: boolean;
}

/**
 * Design:
 *  - One delegated set of listeners on `document` handles every tooltip, so
 *    there is no per-element initialization to duplicate and nothing per
 *    element to tear down.
 *  - A single shared tip element is reused for all tooltips, so the DOM never
 *    grows no matter how many tooltips exist.
 *  - Per-element options live in a WeakMap, so when an anchor element is
 *    garbage collected its options disappear automatically.
 */
export class Tooltip
{
    private static readonly MARKER = "data-w-tooltip";
    private static readonly TIP_ID = "w-tooltip";
    private static readonly registry = new WeakMap<HTMLElement, TooltipEntry>();

    private static tipEl: HTMLElement | null = null;
    private static innerEl: HTMLElement | null = null;
    private static currentAnchor: HTMLElement | null = null;
    private static cleanupAutoUpdate: (() => void) | null = null;

    static attach(node: HTMLElement, title: string | (() => string), html = false): void
    {
        // The anchor can be missing if the header DOM isn't fully rendered yet
        // (callers chain into children that may not exist). A WeakMap key must
        // be an object, so bail out instead of throwing on undefined/null.
        if (!(node instanceof HTMLElement))
        {
            return;
        }

        Tooltip.ensureReady();

        Tooltip.registry.set(node, { title, html });
        node.setAttribute(Tooltip.MARKER, "");

        // Refresh if this element is the one currently shown.
        if (node === Tooltip.currentAnchor)
        {
            Tooltip.show(node);
        }
    }

    /** Create the shared tip element and wire up the delegated listeners once. */
    private static ensureReady(): void
    {
        if (Tooltip.tipEl)
        {
            return;
        }

        Tooltip.tipEl = Dom.createDiv("w-tooltip", "w-tooltip-top");
        Tooltip.tipEl.id = Tooltip.TIP_ID;
        Tooltip.tipEl.setAttribute("role", "tooltip");
        Tooltip.tipEl.style.position = "absolute";
        Tooltip.tipEl.style.top = "0";
        Tooltip.tipEl.style.left = "0";
        Tooltip.tipEl.style.display = "none";

        Dom.addDiv(Tooltip.tipEl, "w-tooltip-arrow");
        Tooltip.innerEl = Dom.addDiv(Tooltip.tipEl, "w-tooltip-inner");

        document.body.appendChild(Tooltip.tipEl);

        document.addEventListener("mouseover", Tooltip.onOver);
        document.addEventListener("focusin", Tooltip.onOver);
        document.addEventListener("mouseout", Tooltip.onOut);
        document.addEventListener("focusout", Tooltip.onOut);
    }

    private static stopAutoUpdate(): void
    {
        if (Tooltip.cleanupAutoUpdate)
        {
            Tooltip.cleanupAutoUpdate();
            Tooltip.cleanupAutoUpdate = null;
        }
    }

    private static show(node: HTMLElement): void
    {
        const entry = Tooltip.registry.get(node);
        let title = entry ? entry.title : undefined;
        if (typeof title === "function")
        {
            title = title();
        }
        if (!title)
        {
            Tooltip.hide();
            return;
        }

        if (entry && entry.html)
        {
            Tooltip.innerEl.innerHTML = title;
        }
        else
        {
            Tooltip.innerEl.textContent = title;
        }

        if (Tooltip.currentAnchor && Tooltip.currentAnchor !== node)
        {
            Tooltip.currentAnchor.removeAttribute("aria-describedby");
        }
        Tooltip.currentAnchor = node;
        // Keep the tip in the same top layer as the anchor: a tip appended to
        // <body> renders behind a modal <dialog> (showModal puts the dialog in
        // the top layer, above everything regardless of z-index).
        const container = node.closest("dialog") || document.body;
        if (Tooltip.tipEl.parentElement !== container)
        {
            container.appendChild(Tooltip.tipEl);
        }
        Tooltip.tipEl.style.display = "block";
        // Let screen readers announce the tooltip text for the focused/hovered element.
        node.setAttribute("aria-describedby", Tooltip.TIP_ID);

        Tooltip.stopAutoUpdate();
        Tooltip.cleanupAutoUpdate = autoUpdate(node, Tooltip.tipEl, () =>
        {
            // The anchor may have been removed from the DOM while visible.
            if (!node.isConnected)
            {
                Tooltip.hide();
                return;
            }
            computePosition(node, Tooltip.tipEl, {
                placement: "top",
                // No shift(): keeps the tooltip centred on the anchor so the
                // CSS-centred arrow keeps pointing at it, matching Bootstrap 3.
                middleware: [offset(0), flip()]
            }).then(position =>
            {
                if (Tooltip.currentAnchor !== node)
                {
                    return;
                }
                Tooltip.tipEl.style.left = position.x + "px";
                Tooltip.tipEl.style.top = position.y + "px";
                // Reflect the resolved placement so the CSS arrow points the right way.
                const base = position.placement.split("-")[0];
                Tooltip.tipEl.classList.remove("w-tooltip-top", "w-tooltip-bottom", "w-tooltip-left", "w-tooltip-right");
                Tooltip.tipEl.classList.add("w-tooltip-" + base);
            });
        });

        // Fade in on the next frame so the opacity transition runs.
        requestAnimationFrame(() =>
        {
            if (Tooltip.currentAnchor === node)
            {
                Tooltip.tipEl.classList.add("w-tooltip-in");
            }
        });
    }

    private static hide(): void
    {
        if (!Tooltip.currentAnchor)
        {
            return;
        }
        Tooltip.stopAutoUpdate();
        Tooltip.currentAnchor.removeAttribute("aria-describedby");
        Tooltip.currentAnchor = null;
        Tooltip.tipEl.classList.remove("w-tooltip-in");
        Tooltip.tipEl.style.display = "none";
    }

    // Shared by mouseover + focusin.
    private static readonly onOver = (e: MouseEvent | FocusEvent): void =>
    {
        const node = Tooltip.closestTooltip(e.target);
        if (node && node !== Tooltip.currentAnchor)
        {
            Tooltip.show(node);
        }
    };

    // Shared by mouseout + focusout.
    private static readonly onOut = (e: MouseEvent | FocusEvent): void =>
    {
        if (!Tooltip.currentAnchor || Tooltip.closestTooltip(e.target) !== Tooltip.currentAnchor)
        {
            return;
        }
        // Keep showing while moving within the anchor (e.g. between its children).
        const to = e.relatedTarget as Node | null;
        if (to && Tooltip.currentAnchor.contains(to))
        {
            return;
        }
        Tooltip.hide();
    };

    private static closestTooltip(target: EventTarget | null): HTMLElement | null
    {
        return target instanceof Element ? target.closest("[" + Tooltip.MARKER + "]") : null;
    }
}
